/** PWA / standalone / mobile viewport helpers — no playback or WebRTC logic. */

const IOS_UA = /iPhone|iPad|iPod/i;
const MOBILE_UA = /Android|iPhone|iPad|iPod/i;

export type DisplayMode = 'browser' | 'standalone' | 'fullscreen' | 'minimal-ui' | 'unknown';

export type ServiceWorkerStatus =
  | 'unsupported'
  | 'not-registered'
  | 'registered'
  | 'controlled'
  | 'error';

export type BrowserContext =
  | 'ios-safari'
  | 'ios-chrome'
  | 'ios-firefox'
  | 'ios-edge'
  | 'ios-other'
  | 'android-chrome'
  | 'desktop-safari'
  | 'desktop-chrome'
  | 'desktop-other';

export interface PwaDiagnostics {
  navigatorStandalone: boolean | null;
  displayModeMediaQuery: boolean;
  displayMode: DisplayMode;
  isStandalone: boolean;
  serviceWorkerStatus: ServiceWorkerStatus;
  serviceWorkerScope: string | null;
  serviceWorkerScriptUrl: string | null;
  manifestLoaded: boolean;
  manifestUrl: string | null;
  manifestDisplay: string | null;
  manifestStartUrl: string | null;
  manifestScope: string | null;
  manifestError: string | null;
  appleMetaPresent: boolean;
  appleMetaContent: string | null;
  manifestLinkHref: string | null;
  currentUrl: string;
  userAgent: string;
  browserContext: BrowserContext;
  isIos: boolean;
  isSafariBrowserTab: boolean;
  isHttps: boolean;
  buildMode: 'development' | 'production';
  verdict: string[];
}

export function isIosDevice(): boolean {
  return IOS_UA.test(navigator.userAgent);
}

export function isMobileDevice(): boolean {
  return MOBILE_UA.test(navigator.userAgent);
}

export function getNavigatorStandalone(): boolean | null {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return typeof nav.standalone === 'boolean' ? nav.standalone : null;
}

export function getDisplayMode(): DisplayMode {
  const modes: DisplayMode[] = ['standalone', 'fullscreen', 'minimal-ui', 'browser'];
  for (const mode of modes) {
    if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode;
  }
  return 'unknown';
}

export function getBrowserContext(): BrowserContext {
  const ua = navigator.userAgent;
  if (isIosDevice()) {
    if (/CriOS/i.test(ua)) return 'ios-chrome';
    if (/FxiOS/i.test(ua)) return 'ios-firefox';
    if (/EdgiOS/i.test(ua)) return 'ios-edge';
    if (/Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) return 'ios-safari';
    return 'ios-other';
  }
  if (/Android/i.test(ua)) return 'android-chrome';
  if (/Safari/i.test(ua) && !/Chrome|Chromium/i.test(ua)) return 'desktop-safari';
  if (/Chrome|Chromium/i.test(ua)) return 'desktop-chrome';
  return 'desktop-other';
}

/** Launched from Home Screen (iOS) or installed PWA (Android/desktop). */
export function isStandalonePwa(): boolean {
  return getNavigatorStandalone() === true || window.matchMedia('(display-mode: standalone)').matches;
}

export function isSafariBrowserTab(): boolean {
  if (isStandalonePwa()) return false;
  const ctx = getBrowserContext();
  return ctx === 'ios-safari' || ctx === 'desktop-safari';
}

async function getServiceWorkerDiagnostics(): Promise<{
  status: ServiceWorkerStatus;
  scope: string | null;
  scriptUrl: string | null;
}> {
  if (!('serviceWorker' in navigator)) {
    return { status: 'unsupported', scope: null, scriptUrl: null };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return { status: 'not-registered', scope: null, scriptUrl: null };
    }

    const scriptUrl = registration.active?.scriptURL ?? registration.installing?.scriptURL ?? registration.waiting?.scriptURL ?? null;
    const status: ServiceWorkerStatus = navigator.serviceWorker.controller
      ? 'controlled'
      : 'registered';

    return { status, scope: registration.scope, scriptUrl };
  } catch {
    return { status: 'error', scope: null, scriptUrl: null };
  }
}

async function getManifestDiagnostics(): Promise<{
  loaded: boolean;
  url: string | null;
  display: string | null;
  startUrl: string | null;
  scope: string | null;
  error: string | null;
}> {
  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  const href = link?.getAttribute('href') ?? null;
  const manifestUrl = href ? new URL(href, window.location.origin).href : null;

  if (!manifestUrl) {
    return {
      loaded: false,
      url: null,
      display: null,
      startUrl: null,
      scope: null,
      error: 'No <link rel="manifest"> in document',
    };
  }

  try {
    const response = await fetch(manifestUrl, { cache: 'no-store' });
    if (!response.ok) {
      return {
        loaded: false,
        url: manifestUrl,
        display: null,
        startUrl: null,
        scope: null,
        error: `Manifest fetch failed: HTTP ${response.status}`,
      };
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json') && !contentType.includes('manifest')) {
      return {
        loaded: false,
        url: manifestUrl,
        display: null,
        startUrl: null,
        scope: null,
        error: `Wrong content-type: ${contentType || '(none)'}`,
      };
    }

    const manifest = (await response.json()) as Record<string, string>;
    return {
      loaded: true,
      url: manifestUrl,
      display: manifest.display ?? null,
      startUrl: manifest.start_url ?? null,
      scope: manifest.scope ?? null,
      error: null,
    };
  } catch (err) {
    return {
      loaded: false,
      url: manifestUrl,
      display: null,
      startUrl: null,
      scope: null,
      error: err instanceof Error ? err.message : 'Manifest fetch failed',
    };
  }
}

function readAppleMeta(): { present: boolean; content: string | null } {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-capable"]');
  return {
    present: meta !== null,
    content: meta?.getAttribute('content') ?? null,
  };
}

export function explainStandaloneState(diagnostics: PwaDiagnostics): string[] {
  if (diagnostics.isStandalone) {
    return ['Running in standalone mode (launched from home screen icon or installed PWA).'];
  }

  const reasons: string[] = [];

  if (!diagnostics.isHttps) {
    reasons.push('Page is not served over HTTPS. iOS requires HTTPS for Add to Home Screen web apps.');
  }

  if (!diagnostics.appleMetaPresent || diagnostics.appleMetaContent !== 'yes') {
    reasons.push(
      'apple-mobile-web-app-capable meta tag is missing or not "yes" in the served HTML. iOS ignores the manifest for standalone and requires this meta tag.',
    );
  }

  if (!diagnostics.manifestLinkHref) {
    reasons.push('No <link rel="manifest"> in the document head.');
  } else if (!diagnostics.manifestLoaded) {
    reasons.push(`Manifest failed to load: ${diagnostics.manifestError ?? 'unknown error'}`);
  } else if (diagnostics.manifestDisplay !== 'standalone') {
    reasons.push(`Manifest display is "${diagnostics.manifestDisplay ?? 'missing'}", not "standalone".`);
  }

  if (diagnostics.browserContext === 'ios-chrome') {
    reasons.push(
      'You are in Chrome on iOS. Standalone web apps only work when added from Safari — open this URL in Safari, then Share → Add to Home Screen.',
    );
  } else if (diagnostics.browserContext === 'ios-firefox' || diagnostics.browserContext === 'ios-edge') {
    reasons.push(
      'You are in a non-Safari browser on iOS. Use Safari to Add to Home Screen, then launch from the home screen icon.',
    );
  } else if (diagnostics.isSafariBrowserTab) {
    reasons.push(
      'You are in a Safari browser tab (navigator.standalone is false). To run standalone: Safari → Share → Add to Home Screen, then open the MatchRoom icon from your home screen — do not open a bookmark or type the URL in Safari.',
    );
  }

  if (
    diagnostics.isIos &&
    diagnostics.navigatorStandalone === false &&
    diagnostics.displayMode === 'browser' &&
    !reasons.some((r) => r.includes('Safari browser tab'))
  ) {
    reasons.push(
      'navigator.standalone is false and display-mode is browser — the page was not launched from the home screen icon.',
    );
  }

  if (diagnostics.serviceWorkerStatus === 'not-registered' && diagnostics.buildMode === 'development') {
    reasons.push(
      'Service worker is not registered yet (may still be installing). Reload after a few seconds.',
    );
  } else if (diagnostics.serviceWorkerStatus === 'not-registered') {
    reasons.push('Service worker is not registered. Check the browser console for registration errors.');
  }

  if (reasons.length === 0) {
    reasons.push(
      'Configuration looks correct but display-mode is still browser. Delete any existing home screen icon, re-add from Safari (Share → Add to Home Screen), then launch only from the new icon.',
    );
  }

  return reasons;
}

export async function collectPwaDiagnostics(): Promise<PwaDiagnostics> {
  const [sw, manifest] = await Promise.all([
    getServiceWorkerDiagnostics(),
    getManifestDiagnostics(),
  ]);
  const apple = readAppleMeta();
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  const displayMode = getDisplayMode();
  const navigatorStandalone = getNavigatorStandalone();

  const diagnostics: PwaDiagnostics = {
    navigatorStandalone,
    displayModeMediaQuery: window.matchMedia('(display-mode: standalone)').matches,
    displayMode,
    isStandalone: isStandalonePwa(),
    serviceWorkerStatus: sw.status,
    serviceWorkerScope: sw.scope,
    serviceWorkerScriptUrl: sw.scriptUrl,
    manifestLoaded: manifest.loaded,
    manifestUrl: manifest.url,
    manifestDisplay: manifest.display,
    manifestStartUrl: manifest.startUrl,
    manifestScope: manifest.scope,
    manifestError: manifest.error,
    appleMetaPresent: apple.present,
    appleMetaContent: apple.content,
    manifestLinkHref: manifestLink?.getAttribute('href') ?? null,
    currentUrl: window.location.href,
    userAgent: navigator.userAgent,
    browserContext: getBrowserContext(),
    isIos: isIosDevice(),
    isSafariBrowserTab: isSafariBrowserTab(),
    isHttps: window.location.protocol === 'https:',
    buildMode: import.meta.env.PROD ? 'production' : 'development',
    verdict: [],
  };

  diagnostics.verdict = explainStandaloneState(diagnostics);
  return diagnostics;
}

export function supportsElementFullscreen(): boolean {
  const el = document.documentElement as HTMLElement & {
    requestFullscreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => void;
  };
  return typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function';
}

/**
 * iPhone Safari cannot reliably hide browser chrome with Element Fullscreen on divs.
 * Use immersive fixed overlay instead. Desktop uses the Fullscreen API.
 */
export function prefersImmersiveCinema(): boolean {
  if (isIosDevice()) return true;
  if (isMobileDevice() && !supportsElementFullscreen()) return true;
  return false;
}

export function isPortrait(): boolean {
  return window.matchMedia('(orientation: portrait)').matches;
}

let viewportCleanup: (() => void) | null = null;

/** Bind --app-height to visualViewport for iOS dynamic toolbars / notch. */
export function installVisualViewportBinding(): () => void {
  viewportCleanup?.();

  const apply = () => {
    const height = window.visualViewport?.height ?? window.innerHeight;
    const offsetTop = window.visualViewport?.offsetTop ?? 0;
    document.documentElement.style.setProperty('--app-height', `${height}px`);
    document.documentElement.style.setProperty('--app-offset-top', `${offsetTop}px`);
  };

  apply();
  window.visualViewport?.addEventListener('resize', apply);
  window.visualViewport?.addEventListener('scroll', apply);
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);

  const cleanup = () => {
    window.visualViewport?.removeEventListener('resize', apply);
    window.visualViewport?.removeEventListener('scroll', apply);
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
    document.documentElement.style.removeProperty('--app-height');
    document.documentElement.style.removeProperty('--app-offset-top');
  };

  viewportCleanup = cleanup;
  return cleanup;
}

export function clearVisualViewportBinding(): void {
  viewportCleanup?.();
  viewportCleanup = null;
}

let landscapeLocked = false;

export async function lockLandscapeForCinema(): Promise<void> {
  if (!isStandalonePwa()) return;
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (orientation: string) => Promise<void>;
  };
  if (typeof orientation?.lock !== 'function') return;
  try {
    await orientation.lock('landscape');
    landscapeLocked = true;
  } catch {
    // Orientation lock requires installed PWA + user gesture on some platforms.
  }
}

export async function unlockLandscapeForCinema(): Promise<void> {
  if (!landscapeLocked) return;
  const orientation = screen.orientation as ScreenOrientation & { unlock?: () => void };
  try {
    orientation.unlock?.();
  } catch {
    // ignore
  }
  landscapeLocked = false;
}

export function markStandaloneDocument(): void {
  if (isStandalonePwa()) {
    document.documentElement.classList.add('pwa-standalone');
  }
}
